import {
  ArcType,
  Cartesian3,
  Color,
  GeometryInstance,
  JulianDate,
  Material,
  Primitive,
  PolylineCollection,
  PolylineColorAppearance,
  PolylineGeometry,
  ShowGeometryInstanceAttribute,
} from "cesium";
import { getClimbColorRgba } from "./getClimbColorRgba";

/**
 * Vario-coloured 3D track.
 *
 * Rendered as ONE batched `Primitive` (per-segment vertex colours) plus a tiny
 * `PolylineCollection` for the leading edge. Colours stay pixel-exact per fix —
 * nothing is merged or smoothed — and the track still "draws on" up to the
 * marker as the clock advances.
 *
 * ## Why not one Polyline per fix
 *
 * The obvious version — a `PolylineCollection` with one coloured polyline per
 * GPS fix — is a frame killer, and not for the reason you'd guess. Cesium
 * builds its draw commands by walking the polylines in order and starting a NEW
 * command every time the material *id* changes (`createMaterialId` =
 * `type:JSON.stringify(uniforms)`, i.e. the colour). A vario track changes
 * colour at nearly every fix, so:
 *   - one draw command per segment (measured: 31 216 commands), and
 *   - a `JSON.stringify` per polyline PER FRAME (measured: 86 356 polylines).
 * That measured **222 ms/frame (≈4.5 fps)** on an M5 Max; hiding the track
 * alone took the same scene to **1.2 ms/frame**. Terrain LOD, MSAA, FXAA and
 * resolution scale made no measurable difference — it was all this.
 *
 * ## The layout used instead
 *
 * - **History**: the whole track as `Primitive` + `PolylineColorAppearance`,
 *   split into chunks of `_chunkSize` segments, one `GeometryInstance` each.
 *   Batched into a handful of draw calls with ~zero per-frame CPU; the reveal
 *   flips a per-instance `show` attribute (a batch-table write) when the clock
 *   crosses a chunk boundary.
 * - **Leading edge**: only the segments of the chunk being flown right now live
 *   in a `PolylineCollection`, so the draw-on stays exact per fix. It is at most
 *   `_chunkSize` polylines, rebuilt on each chunk crossing.
 *
 * Each leading-edge polyline needs its OWN `Material`: Cesium destroys a
 * polyline's material on teardown, so a shared instance double-destroys
 * ("This object was destroyed").
 *
 * `czmlHelper.js` colored mode therefore emits only the marker/label;
 * `createColorSegments` is no longer called.
 */

// Target number of history chunks. Fewer, bigger chunks = fewer geometry
// instances to build; smaller chunks = a shorter live leading edge per frame.
const TARGET_CHUNKS = 1500;
const MIN_CHUNK = 32;
const MAX_CHUNK = 256;
const LINE_WIDTH = 2;

class VarioTrack {
  constructor(viewer) {
    this.viewer = viewer;
    this.primitive = null; // batched history
    this.edge = null; // PolylineCollection for the chunk in progress
    this.edgeLines = []; // its polylines, index i = segment _edgeStart + i
    this.positions = []; // Cartesian3 per fix
    this.segColors = []; // Color per segment (segment i connects fix i → i+1)
    this.segTimes = []; // relative seconds of each segment's start
    this.startJulian = null;
    this.chunkSize = MIN_CHUNK;
    this.chunkCount = 0;
    this.lastCurSeg = -1; // last segment index revealed
    this._edgeChunk = -2; // chunk currently held by the leading edge
    this._shownChunks = -1; // history chunks [0..n] currently flagged visible
    this._appliedChunks = -1; // …and what the primitive actually has (async)
    this.flightId = null;
    this._removeTick = null;
  }

  /** Show the colored track for a flight. `data` is the parsed fix array. */
  show(flightId, data) {
    this.clear();
    if (!data || data.length < 2) return;
    this.flightId = flightId;
    this.startJulian = JulianDate.fromDate(new Date(data[0].epoch * 1000));

    const n = data.length;
    const epoch0 = data[0].epoch;
    const positions = new Array(n);
    const segColors = new Array(n - 1);
    const segTimes = new Array(n - 1);

    for (let i = 0; i < n; i++) {
      // dAltitude is the terrain-clamped display altitude (helper/terrainClamp.js),
      // set only where the recorded one would draw inside the hill.
      const alt = data[i].dAltitude != null ? data[i].dAltitude : data[i].altitude;
      positions[i] = Cartesian3.fromDegrees(data[i].lng, data[i].lat, alt);
    }
    for (let i = 0; i < n - 1; i++) {
      segTimes[i] = data[i].epoch - epoch0;
      const rgba = getClimbColorRgba(data[i].vSpeed);
      segColors[i] = Color.fromBytes(rgba[0], rgba[1], rgba[2], rgba[3] == null ? 255 : rgba[3]);
    }

    this.positions = positions;
    this.segColors = segColors;
    this.segTimes = segTimes;

    const segCount = n - 1;
    this.chunkSize = Math.min(
      MAX_CHUNK,
      Math.max(MIN_CHUNK, Math.round(segCount / TARGET_CHUNKS))
    );
    this.chunkCount = Math.ceil(segCount / this.chunkSize);

    const instances = new Array(this.chunkCount);
    for (let c = 0; c < this.chunkCount; c++) {
      const first = c * this.chunkSize; // first segment of the chunk
      const last = Math.min(first + this.chunkSize, segCount); // one past its last
      instances[c] = new GeometryInstance({
        id: c,
        geometry: new PolylineGeometry({
          positions: positions.slice(first, last + 1),
          colors: segColors.slice(first, last),
          colorsPerVertex: false, // flat per segment → pixel-exact per fix
          width: LINE_WIDTH,
          arcType: ArcType.NONE, // straight 3D segments, no geodesic subdivision
          vertexFormat: PolylineColorAppearance.VERTEX_FORMAT,
        }),
        attributes: { show: new ShowGeometryInstanceAttribute(false) },
      });
    }

    this.primitive = new Primitive({
      geometryInstances: instances,
      appearance: new PolylineColorAppearance({ translucent: false }),
      asynchronous: true, // combine on a worker; nothing blocks the first frames
      allowPicking: false,
    });
    this.edge = new PolylineCollection();
    this.viewer.scene.primitives.add(this.primitive);
    this.viewer.scene.primitives.add(this.edge);

    this._removeTick = this.viewer.clock.onTick.addEventListener(() => this._reveal());
    this._reveal(); // reveal up to the current clock time immediately
  }

  // Last segment index whose start time has been reached (binary search).
  _curSeg(rel) {
    const t = this.segTimes;
    if (t.length === 0 || rel < t[0]) return -1;
    if (rel >= t[t.length - 1]) return t.length - 1;
    let lo = 0;
    let hi = t.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (t[mid] <= rel) lo = mid;
      else hi = mid - 1;
    }
    return lo;
  }

  _reveal() {
    if (!this.primitive) return;
    const rel = JulianDate.secondsDifference(this.viewer.clock.currentTime, this.startJulian);
    const curSeg = this._curSeg(rel);

    if (curSeg !== this.lastCurSeg) {
      const chunk = curSeg < 0 ? -1 : Math.floor(curSeg / this.chunkSize);
      if (chunk !== this._edgeChunk) this._buildEdge(chunk);
      this._revealEdge(curSeg);
      this._shownChunks = chunk; // every chunk before the live one is complete
      this.lastCurSeg = curSeg;
    }

    // The primitive only accepts attribute writes once its worker is done.
    if (this._appliedChunks !== this._shownChunks && this.primitive.ready) {
      this._applyChunkShows();
    }
  }

  /** Move the live leading edge to `chunk` (-1 = before the track starts). */
  _buildEdge(chunk) {
    this.edge.removeAll(); // destroys the old polylines + their materials
    this.edgeLines = [];
    this._edgeChunk = chunk;
    this._edgeStart = chunk < 0 ? 0 : chunk * this.chunkSize;
    if (chunk < 0) return;

    const last = Math.min(this._edgeStart + this.chunkSize, this.segColors.length);
    for (let i = this._edgeStart; i < last; i++) {
      this.edgeLines.push(
        this.edge.add({
          positions: [this.positions[i], this.positions[i + 1]],
          width: LINE_WIDTH,
          material: Material.fromType("Color", { color: this.segColors[i] }),
          show: false,
        })
      );
    }
  }

  /** Within the live chunk, show the segments already flown. */
  _revealEdge(curSeg) {
    const lines = this.edgeLines;
    const upTo = curSeg - this._edgeStart; // last local index to show
    for (let i = 0; i < lines.length; i++) {
      const want = i <= upTo;
      if (lines[i].show !== want) lines[i].show = want;
    }
  }

  /** Flip the per-instance show flags of the completed history chunks. */
  _applyChunkShows() {
    const from = Math.min(this._appliedChunks, this._shownChunks);
    const to = Math.max(this._appliedChunks, this._shownChunks);
    for (let c = Math.max(0, from); c <= to && c < this.chunkCount; c++) {
      const attributes = this.primitive.getGeometryInstanceAttributes(c);
      if (!attributes) continue;
      const want = c < this._shownChunks ? 1 : 0;
      if (attributes.show[0] !== want) attributes.show = [want];
    }
    this._appliedChunks = this._shownChunks;
  }

  /** Show/hide the whole track (keeps the reveal state underneath). */
  setVisible(visible) {
    if (this.primitive) this.primitive.show = visible;
    if (this.edge) this.edge.show = visible;
  }

  clear() {
    if (this._removeTick) {
      this._removeTick();
      this._removeTick = null;
    }
    if (this.primitive) {
      this.viewer.scene.primitives.remove(this.primitive); // also destroys it
      this.primitive = null;
    }
    if (this.edge) {
      this.viewer.scene.primitives.remove(this.edge);
      this.edge = null;
    }
    this.edgeLines = [];
    this.positions = [];
    this.segColors = [];
    this.segTimes = [];
    this.startJulian = null;
    this.lastCurSeg = -1;
    this._edgeChunk = -2;
    this._edgeStart = 0;
    this._shownChunks = -1;
    this._appliedChunks = -1;
    this.flightId = null;
  }
}

export function getVarioTrack(viewer) {
  if (!viewer.__varioTrack) viewer.__varioTrack = new VarioTrack(viewer);
  return viewer.__varioTrack;
}
